import { Box, styled } from '@mui/material';
import { lazy, Suspense, useState } from 'react';
import { Outlet, useLocation, useMatches } from 'react-router';

import { SIZES } from '../../assets/constants/constants.ts';
import useBrainSession from '../../services/BrainSession/useBrainSession.ts';
import { GlobalAlert } from '../GlobalAlert/index.ts';
import Loader from '../Loader/index.ts';

import Footer from './_components/Footer/index.ts';
import SideBar from './_components/SideBar/index.ts';
import TopBar from './_components/TopBar/index.ts';

const ChatContainer = lazy(() => import('../Chat/_components/ChatContainer/index.ts'));

const LayoutRoot = styled(Box)(({ theme }) => ({
  height: '100%',
  width: '100%',
  '--top-bar-height': `${SIZES.topBar.height.small}px`,
  [theme.breakpoints.up(SIZES.navigationChange)]: {
    '--top-bar-height': `${SIZES.topBar.height.large}px`,
  },
}));

const LayoutWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  flex: '1 1 auto',
  // hiding overflow prevents sticky element from sticking, disabling it for now
  // overflow: 'hidden',
  height: '100%',
  flexGrow: 1,
});

const ContentContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,
});

const Content = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
});

const DashboardLayout = () => {
  const location = useLocation();
  const isMyScoopr = location.pathname.startsWith('/min-scoopr');

  const { showChatContainer } = useBrainSession();
  const [isSideBarOpen, setSideBarOpen] = useState(false);

  const matches = useMatches();
  const hideLayout = matches.some((match) => (match.handle as any)?.hideLayout);

  if (hideLayout) {
    return <Outlet />;
  }

  return (
    <LayoutRoot>
      {/* Be very careful with conditional suspense boundaries,
          it has to be a direct child of a real DOM element 
          or else hydration mismatches can produce weird bugs
          like both branches being rendered at the same time */}
      {showChatContainer ? (
        <Suspense fallback={<Loader />}>
          {/* Page is unmounted, meta tags are missing, remove this if a global SEO handler is introduced */}
          <title>Scoopr</title>
          <ChatContainer />
        </Suspense>
      ) : (
        <>
          <TopBar
            setSideBarOpen={() => setSideBarOpen(!isSideBarOpen)}
            isSideBarOpen={isSideBarOpen}
          />
          <SideBar onSideBarClose={() => setSideBarOpen(false)} isSideBarOpen={isSideBarOpen} />

          <LayoutWrapper>
            <ContentContainer role="main">
              <Content>
                <GlobalAlert />
                <Outlet />
              </Content>
            </ContentContainer>
          </LayoutWrapper>
          <div className={isMyScoopr ? 'hidden md:block' : 'block'}>
            <Footer />
          </div>
        </>
      )}
    </LayoutRoot>
  );
};

export default DashboardLayout;
